import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

export default function SolicitarCotizacion() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/">
                <img 
                  src="https://static.readdy.ai/image/988cd9015f1a752aa44f40d73592c8cb/d03d7dd558dd59549242c09cb047b58f.png" 
                  alt="IKIGAI Global Group Inc." 
                  className="h-14 w-auto cursor-pointer"
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-10 items-center">
              <Link to="/" className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap font-medium text-sm uppercase tracking-wide transition-colors">{t('nav.home', 'Inicio')}</Link>
              <Link to="/#productos" className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap font-medium text-sm uppercase tracking-wide transition-colors">{t('nav.products', 'Productos')}</Link>
              <Link to="/#marcas" className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap font-medium text-sm uppercase tracking-wide transition-colors">{t('nav.brands', 'Marcas')}</Link>
              <Link to="/#contacto" className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap font-medium text-sm uppercase tracking-wide transition-colors">{t('nav.contact', 'Contacto')}</Link>
              
              {/* Language Toggle */}
              <button 
                onClick={toggleLanguage}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <i className="ri-global-line text-gray-600"></i>
                <span className="text-sm font-medium text-gray-700">
                  {i18n.language === 'es' ? 'EN' : 'ES'}
                </span>
              </button>

              <Link to="/solicitar-cotizacion" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm">
                Cotización Gratuita
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className="ri-menu-line text-xl text-gray-700"></i>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-2">
                <Link to="/" className="text-gray-700 hover:text-blue-600 cursor-pointer py-2 font-medium">{t('nav.home', 'Inicio')}</Link>
                <Link to="/#productos" className="text-gray-700 hover:text-blue-600 cursor-pointer py-2 font-medium">{t('nav.products', 'Productos')}</Link>
                <Link to="/#marcas" className="text-gray-700 hover:text-blue-600 cursor-pointer py-2 font-medium">{t('nav.brands', 'Marcas')}</Link>
                <Link to="/#contacto" className="text-gray-700 hover:text-blue-600 cursor-pointer py-2 font-medium">{t('nav.contact', 'Contacto')}</Link>
                <button 
                  onClick={toggleLanguage}
                  className="flex items-center space-x-2 px-3 py-2 text-left"
                >
                  <i className="ri-global-line text-gray-600"></i>
                  <span className="text-sm font-medium text-gray-700">
                    {i18n.language === 'es' ? 'English' : 'Español'}
                  </span>
                </button>
                <Link to="/solicitar-cotizacion" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm mt-4 text-center">
                  Cotización Gratuita
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section with Office Image */}
      <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800 text-white py-24"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/988cd9015f1a752aa44f40d73592c8cb/354a1669bd41cda327c0534abdf55ecf.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-gray-900/85 to-blue-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-blue-600/20 border border-blue-400/30 rounded-full text-blue-200 text-sm font-medium uppercase tracking-wide">
              Distribución Directa desde Miami
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight">
            Solicitar Cotización
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Complete el formulario y reciba una cotización personalizada en menos de 24 horas. 
            Nuestro equipo de expertos está listo para ayudarle.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quote Form */}
          <div className="bg-white p-10 rounded-3xl shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Formulario de Cotización</h3>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Su nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Apellido *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Su apellido"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Corporativo *</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="empresa@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono *</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="+1 (305) 000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">País *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="País de operación"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Empresa *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Nombre de su empresa"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Producto de Interés *</label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8">
                  <option>Seleccione un producto</option>
                  <option>Newlong DS-2 - Cerradora Industrial</option>
                  <option>Newlong DS-2II - Cerradora Mejorada</option>
                  <option>Newlong DS-9 - Alta Velocidad</option>
                  <option>Union Special 80800</option>
                  <option>Repuestos Newlong</option>
                  <option>Agujas Organ</option>
                  <option>Hilo Poliéster Industrial</option>
                  <option>Motores y Mesas</option>
                  <option>Otro (especificar en detalles)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cantidad Estimada</label>
                <input 
                  type="number" 
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Número de unidades"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Detalles Adicionales *</label>
                <textarea 
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Describa sus necesidades específicas, aplicación industrial, tiempo de entrega requerido, etc."
                ></textarea>
              </div>

              <button 
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-bold transition-all whitespace-nowrap hover:shadow-lg text-lg"
              >
                <i className="ri-send-plane-fill mr-2"></i>
                Enviar Solicitud de Cotización
              </button>

              <p className="text-sm text-gray-500 text-center">
                * Campos obligatorios. Responderemos en menos de 24 horas hábiles.
              </p>
            </form>
          </div>

          {/* Contact Info Below Form */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">
              ¿Prefiere contactarnos directamente?
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-fill text-2xl text-green-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-1">Teléfono</h5>
                <p className="text-gray-600 font-semibold">+11 (305) 792-8462</p>
              </div>
              <div className="text-center p-4">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-fill text-2xl text-blue-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-1">Email</h5>
                <p className="text-gray-600 font-semibold text-sm">Ikigaigroupusa@gmail.com</p>
              </div>
              <div className="text-center p-4">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-fill text-2xl text-purple-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-1">Horario</h5>
                <p className="text-gray-600">Lun - Vie: 8AM - 6PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <img 
                src="https://static.readdy.ai/image/988cd9015f1a752aa44f40d73592c8cb/d03d7dd558dd59549242c09cb047b58f.png" 
                alt="IKIGAI Global Group Inc." 
                className="h-12 w-auto mb-6"
              />
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Distribución directa de fábrica de maquinaria industrial para coser sacos y repuestos originales. 
                Su socio estratégico en equipos industriales de alta calidad desde Miami hacia toda Latinoamérica.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 cursor-pointer transition-colors">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 cursor-pointer transition-colors">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 cursor-pointer transition-colors">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Productos</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Cerradoras Newlong</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Cosedoras Industriales</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Máquinas Union Special</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Repuestos Originales</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Accesorios</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Servicios</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Asesoría Especializada</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Consultoría</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Mantenimiento</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Capacitación</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer transition-colors">Logística</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-gray-400">
            <p>&copy; 2024 IKIGAI Global Group Inc. Todos los derechos reservados.</p>
            <a href="https://readdy.ai/?ref=logo" className="hover:text-white transition-colors mt-4 md:mt-0">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
